import { useEffect, useState } from "react";
import { Settings, Shield, ShieldOff, Circle, XCircle } from "react-feather";
import clsx from "clsx";
import { PublicKey } from "@solana/web3.js";
import * as solana from "@solana/web3.js";
import * as beet from "@metaplex-foundation/beet";
import { useConnection } from "@solana/wallet-adapter-react";
import { BN } from "bn.js";
import useLanguage from "@axvdex/hooks/useLanguage";
import * as nicknameGen from "@axvdex/solana/nicknameAPI/js/src/generated";
import {
  SOL_PUBLIC_KEY,
  fetchSolanaFeesMap,
  fetchSolanaNicknamesMap,
  requestAddProtectionSolana,
  requestUpdateNicknameSolana,
  useNicknameMap,
  useUserBalances,
} from "@axvdex/hooks/solana/useNickname";
import { nicknameActionToEnum } from "@axvdex/types/solana/nickname";
import { useAppDispatch, useAppSelector } from "@axvdex/state";
import { selectWalletInfo } from "@axvdex/state/wallet/walletSelectors";
import styles from "@axvdex//styles/MyAssetsManageTokensModal.module.scss";
import { sendToast } from "@axvdex/state/wallet/walletSlice";
import { useUserSettings } from "@axvdex/hooks/useUserSettings";
import Button, { ButtonProps } from "../common/Button";
import CustomModal from "../common/CustomModal";
import CustomSelect, { ListItemsProps } from "../form-element/CustomSelect";
import CustomInput, { CustomInputProps } from "../form-element/CustomInput";
import CustomInputButton from "../form-element/CustomInputButton";
import CustomLoader from "../common/CustomLoader";

type UserSettingsModalProps = {
  style?: "icon" | "button";
  buttonTitle?: string;
};

function UserSettingsModal({ style = "icon", buttonTitle = "Settings" }: UserSettingsModalProps) {
  const { i18 } = useLanguage();
  const { connection } = useConnection();
  const dispatch = useAppDispatch();

  const startingFees = { actionFees: new Map<nicknameGen.ActionType, nicknameGen.TokenFees>([]) };
  const { userSettings, setUserSettings } = useUserSettings();
  const { nicknames: nicknamesListener } = useNicknameMap();

  const [isOpen, setIsOpen] = useState(false);
  const [isWillingToPayForAction, setIsWillingToPayForAction] = useState(false);
  const [nickname, setNickname] = useState(""); // TODO: is nickname different from initial -> disable button
  const [loading, setLoading] = useState(false);
  const [tokenCosts, setTokenCosts] = useState(startingFees);
  const [nicknamesMap, setNicknamesMap] = useState(new Map<solana.PublicKey, nicknameGen.NicknameInfo>());
  const [actionType, setActionType] = useState(nicknameGen.ActionType.OverrideOwnNicknameByOther);
  const [fee, setFee] = useState<Partial<beet.bignum>>(0);
  const [selectedMint, setSelectedMint] = useState(SOL_PUBLIC_KEY);
  const balances = useUserBalances();
  const [selectedNoNicknames, setSelectedNoNicknames] = useState(false);
  const [hasProtection, setHasProtection] = useState(false);
  const walletInfo = useAppSelector(selectWalletInfo);

  const payment: nicknameGen.Payment = {
    mintAddress: selectedMint,
    amount: new BN(fee.valueOf().toString()),
  };

  //const allTokenOptions = ["SOL", "AXVS"]
  const allTokenOptions: ListItemsProps[] = [
    { label: "SOL", value: SOL_PUBLIC_KEY.toString() },
    // { label: "AXVS", value: SOL_PUBLIC_KEY.toString() }, TODO: add AXVS
  ];

  // I have this outside of the return statement due to the Dynamic nature of it
  // Should be modeled with the rest of the 'parent' model
  const nicknameInputProps: CustomInputProps = {
    name: "nickname",
    labelText: i18("Nickname (A-Z, 0-9, _ and - with 20 characters max.)", "userWallet.settings.nicknameDetail"),
    title: i18(`Nickname`, "userWallet.settings.nickname"),
    value: nickname,
    // TODO: disable if ActionType == GetOwnProtection
    onChange: e => typeNicknameFiltered(setNickname, setIsWillingToPayForAction, e),
  };

  const setProtectionButtonProps: ButtonProps = {
    title: "Nickname Protection",
    btnColor: "gradient",
    style: { marginTop: "auto" },
    onClick: () => {
      // TOGGLE
      if (actionType == nicknameGen.ActionType.GetOwnProtection) {
        const userWalletAddress = new PublicKey(walletInfo.walletAddress);
        const newActionType = match_action_type(userWalletAddress, userWalletAddress, nicknamesMap);
        setActionType(newActionType);
      } else {
        setActionType(nicknameGen.ActionType.GetOwnProtection);
      }
    },
  };

  const submitNicknameForm = async () => {
    if (!walletInfo.isConnected) {
      dispatch(
        sendToast({ type: "tx-fail", info: { msg: "Wallet not connected!", toastID: "" + new Date().getTime() } })
      );
      return;
    }
    setLoading(true);
    try {
      const userWalletAddress = new PublicKey(walletInfo.walletAddress);
      if (actionType === nicknameGen.ActionType.GetOwnProtection) {
        await requestAddProtectionSolana(connection, userWalletAddress, payment, userWalletAddress);
      } else {
        await requestUpdateNicknameSolana(connection, userWalletAddress, nickname, payment, userWalletAddress);
      }
      setIsOpen(false);
    } catch (e) {
      dispatch(
        sendToast({ type: "tx-fail", info: { msg: "Transaction failed!", toastID: "" + new Date().getTime() } })
      );
    } finally {
      setLoading(false);
      setIsWillingToPayForAction(false);
    }
  };

  useEffect(() => {
    if (walletInfo.isConnected) {
      const userWalletAddress = new PublicKey(walletInfo.walletAddress);
      setInitialNicknamesAndUserNicknameFromChain(
        connection,
        setNickname,
        setNicknamesMap,
        setHasProtection,
        userWalletAddress
      );
    }
  }, []);

  useEffect(() => {
    setupSolanaFees(connection, setTokenCosts, setFee, actionType, SOL_PUBLIC_KEY);
  }, []);

  useEffect(() => {
    const newFee: Partial<beet.bignum> = askSolanaActionFee(tokenCosts, actionType, selectedMint);
    if (newFee) {
      setFee(newFee);
    } else {
      console.error("fee is undefined");
    }
  }, [actionType, selectedMint, tokenCosts]);

  useEffect(() => {
    const storedValue = userSettings.noNicknames;
    if (storedValue) {
      setSelectedNoNicknames(true);
    }
  }, [userSettings]);

  if (!walletInfo.isConnected) {
    // Just a simple protection for when the user disconnects
    return null;
  }

  return (
    <>
      {style === "button" ? (
        <Button
          title={i18(`User Settings`, "userWallet.settings.title")}
          btnColor={
            nicknamesListener[walletInfo.walletAddress]
              ? nicknamesListener.get(walletInfo.walletAddress).changedBy.toBase58() !== walletInfo.walletAddress
                ? "gradientOrange"
                : "dark-medium"
              : "gradient"
          }
          icon={<Settings />}
          onClick={() => setIsOpen(!isOpen)}
          btnSize="sm"
        >
          {buttonTitle}
        </Button>
      ) : (
        <Button
          style={{ height: "2em" }}
          title={i18(`User Settings`, "userWallet.settings.title")}
          btnVariant="icon"
          icon={<Settings />}
          onClick={() => setIsOpen(!isOpen)}
        />
      )}
      <CustomModal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        extraClassName={clsx(styles.permitAuthenticationModal, "permitAuthenticationModal")}
      >
        <section className="sectionModalHeader">
          <h2 className="h2">{i18("User Settings", "userWallet.settings.title")}</h2>
          <h4>{nicknamesListener.get(walletInfo.walletAddress)?.nickname || "No Nickname"}</h4>
        </section>
        <section className="sectionModalContent withGradientBorderBottom">
          <div className="inlineFlexbox" style={{ width: "100%", placeContent: "space-between" }}>
            <CustomInput {...nicknameInputProps} />
            {nickname !== "" && !hasProtection && (
              <Button {...setProtectionButtonProps}>
                {actionType !== nicknameGen.ActionType.GetOwnProtection ? <ShieldOff /> : <Shield color="green" />}
              </Button>
            )}
          </div>
          <br />
          <br />
          <CustomInputButton
            type="radio"
            id={"noNicknames"}
            name="noNicknames"
            labelText={"Hide nicknames"}
            labelIcon={selectedNoNicknames ? <XCircle /> : <Circle />}
            onClick={async () => {
              setUserSettings("noNicknames", !selectedNoNicknames);
              setSelectedNoNicknames(!selectedNoNicknames);
            }}
          />
          {/* TODO HERE: https://github.com/axvdex/FrontEndWS/blob/main/src/components/modals/UserSettingsModal.tsx#L195-L219 */}
        </section>
        <div>
          <CustomSelect
            name="select_payment_token"
            labelText="Token Payment"
            hiddenLabel={true}
            placeholder="Payment Token - SOL"
            items={allTokenOptions}
            onChange={(option: ListItemsProps) => {
              const asPK = new PublicKey(option.value);
              setSelectedMint(asPK);
            }}
            required
          />
        </div>
        <div className="inlineFlexbox" style={{ width: "100%" }}>
          <div className="inlineFlexbox" style={{ width: "65%" }}>
            <input
              type="checkbox"
              checked={isWillingToPayForAction}
              onChange={e => setIsWillingToPayForAction(e.target.checked)}
            />
            <span>
              {nicknameGen.ActionType.GetOwnProtection === actionType
                ? "Pay to protect your nickname from being changed by others"
                : !nicknamesListener.get(walletInfo.walletAddress)
                ? "Pay to set your nickname"
                : `Pay to overwrite your nickname that was changed by ${
                    nicknamesListener.get(nicknamesListener.get(walletInfo.walletAddress).changedBy.toBase58())
                      ? nicknamesListener.get(nicknamesListener.get(walletInfo.walletAddress).changedBy.toBase58())
                          .nickname
                      : "other user"
                  }`}
            </span>
          </div>
          <div style={{ marginLeft: "auto", textAlign: "center", marginTop: 16 }}>
            {loading ? (
              <CustomLoader size="xs" />
            ) : (
              <Button
                btnColor="gradient"
                title={"Update"}
                style={{ marginBottom: ".5em" }}
                onClick={async () => {
                  await submitNicknameForm();
                }}
                disabled={!isWillingToPayForAction}
              >
                <div>Update:</div>
                <br />
                <div>
                  <small>
                    {fee.toString()} {displayToken(selectedMint)}
                  </small>
                </div>
              </Button>
            )}
            <br />
            <div>Balance: {balances.get(selectedMint.toString())}</div>
          </div>
        </div>
      </CustomModal>
    </>
  );
}

export default UserSettingsModal;

// HELPERS ====================================================================

export function typeNicknameFiltered(
  setNickname: (s: string) => void,
  setCheckboxConfirmation: (b: boolean) => void,
  event
) {
  const regexPattern = /^[A-Za-z0-9_-]{0,16}$/;
  if (regexPattern.test(event.target.value)) {
    setNickname(event.target.value);
    setCheckboxConfirmation(false);
  }
}

export async function setupSolanaFees(
  connection: solana.Connection,
  setterStore,
  setterFee: (fee: Partial<beet.bignum>) => void,
  action: nicknameGen.ActionType,
  initialTokenMint: solana.PublicKey
) {
  const fees = await fetchSolanaFeesMap(connection);
  if (!fees) {
    console.warn("feesStore is undefined");
    return;
  }
  setterStore(fees.feesStore);
  const fee = askSolanaActionFee(fees.feesStore, action, initialTokenMint);
  setterFee(fee);
}

export async function setInitialNicknamesAndUserNicknameFromChain(
  connection: solana.Connection,
  setterUserNickname,
  setterNicknameMap,
  setHasProtection,
  walletAddress: solana.PublicKey
) {
  const nicknameStore = await fetchSolanaNicknamesMap(connection);
  const nickMap = convertMapKeysToStrings(nicknameStore);
  const nicknameExists = nickMap.get(walletAddress.toString());
  if (nicknameExists) {
    setterUserNickname(nicknameExists.nickname);
    setHasProtection(nicknameExists.isProtected);
  }
  setterNicknameMap(nicknameStore);
}

// Function to convert the original map to a new map with string keys
export function convertMapKeysToStrings<T>(map: Map<solana.PublicKey, T>): Map<string, T> {
  const newMap = new Map<string, T>();

  map.forEach((value, key) => {
    newMap.set(key.toString(), value);
  });

  return newMap;
}

export function askSolanaActionFee(
  feesStore: nicknameGen.ActionTokenFees,
  action: nicknameGen.ActionType,
  tokenMint: PublicKey
): Partial<beet.bignum> {
  const actionExists = feesStore.actionFees.get(nicknameActionToEnum(action));
  if (actionExists) {
    const fees2 = convertMapKeysToStrings(actionExists.fees);
    const mintExists = fees2.get(tokenMint.toString());
    if (mintExists) {
      return mintExists;
    }
    console.warn("mint no exist");
  } else {
    console.warn("action does not exist", feesStore);
  }
}

export function displayToken(publicKey) {
  // if publicKey === tokenMint {
  //     return "AXVS"
  // }
  return "SOL";
}

// Mirrors backend logic
export function match_action_type(
  sender: solana.PublicKey,
  targetKey: solana.PublicKey,
  nicknamesMap: Map<solana.PublicKey, nicknameGen.NicknameInfo>
): nicknameGen.ActionType {
  const stringKeyMap = convertMapKeysToStrings(nicknamesMap);
  const nicknameExists: nicknameGen.NicknameInfo = stringKeyMap.get(sender.toString());
  if (nicknameExists) {
    if (sender.toString() === targetKey.toString()) {
      if (nicknameExists.changedBy.toString() === sender.toString()) {
        return nicknameGen.ActionType.OverrideOwnNickname;
      }
      return nicknameGen.ActionType.OverrideOwnNicknameByOther;
    }
    return nicknameGen.ActionType.OverrideOtherNickname;
  }
  if (sender.toString() === targetKey.toString()) {
    return nicknameGen.ActionType.InitSetOwnNickname;
  }
  return nicknameGen.ActionType.InitSetOtherNickname;
}
